import {
  Component,
  OnInit,
  ViewEncapsulation,
  effect,
  inject,
  signal
} from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { AuthStatus, AuthenticatorServiceFacade } from '@aws-amplify/ui';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { filter, pairwise } from 'rxjs/operators';
import { loadRaygunSettings } from './app.raygun.setup';
import { BrandingService } from './branding';
import { AuthService, currentAuthenticatedUser } from './services';
import { parseJWT, sleep } from './utilities';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class AppComponent implements OnInit {
  private authStatus = signal<AuthStatus>(null);
  private router = inject(Router);
  private authService = inject(AuthService);
  private amplify = inject(AuthenticatorService);
  private brand = inject(BrandingService);

  constructor() {
    effect(
      () => {
        const authStatus = this.authStatus();
        console.log(authStatus);
        switch (authStatus) {
          case 'unauthenticated':
            this.navigate('/login');
            break;
          case 'authenticated':
            const { pathname, search } = location;
            this.ssoLoginStatus();
            this.navigate(pathname === '/login' ? '/' : pathname + search);
            break;
          default:
            break;
        }
      },
      { allowSignalWrites: true }
    );

    this.amplify.subscribe((state: AuthenticatorServiceFacade) => {
      this.authStatus.set(state.authStatus);
    });

    // When navigating away from the admin section, reload the target page
    // In particular, the main page doesn't properly reload on navigation
    this.router.events
      .pipe(
        filter((evt: any) => evt instanceof RoutesRecognized),
        pairwise()
      )
      .subscribe(async (events: RoutesRecognized[]) => {
        const previousUrl = events[0]?.urlAfterRedirects;
        const currentUrl = events[1]?.urlAfterRedirects;
        if (!(previousUrl && currentUrl)) {
          // should never happen with pairwise()
          console.warn('Could not determine previous and current URLs');
          return;
        }
        const forceReload = [
          '/admin',
          '/perseus-admin',
          '/monitor',
          '/user-management',
          '/static-data-management'
        ].some(section => {
          return (
            previousUrl.startsWith(section) && !currentUrl.startsWith(section)
          );
        });

        if (forceReload) {
          console.log(
            `Navigating away from ${previousUrl} section, reloading`,
            currentUrl
          );
          location.href = currentUrl;
        }
      });
  }

  public ngOnInit(): void {
    loadRaygunSettings();
  }

  private navigate(url: string) {
    this.router.navigateByUrl(url);
  }

  private async ssoLoginStatus() {
    try {
      const user = await currentAuthenticatedUser();
      console.log('Cognito user', user);
      const session = await this.authService.currentSession();
      const jwt = parseJWT(session.getIdToken().getJwtToken());
      console.log(jwt);
      const group = jwt['cognito:groups']?.[0];
      if (group) {
        console.log('User group:', group);
        this.brand.setBrand(group);
      }
    } catch (e) {
      console.warn('No current cognito session:', e);
    }
  }
}
